import { useCallback, useEffect, useRef, useState } from 'react';

import { TwoPaneModal } from '@rocco/components/two-pane-modal';
import { cn } from '@rocco/utils/cn';
import { useUserLoggedIn } from '@modules/user';
import { AuthModalBrandPane } from '~/modules-rocco/user/component/auth-modal/AuthModalBrandPane';
import { getAuthBrandStatement } from '~/modules-rocco/user/component/auth-modal/auth-intent-copy';
import { isReturningPasskeyDevice } from '~/modules/user/passkey/passkey-device-match';
import { useAuthGateSegment } from '~/modules-rocco/user/auth-gate/use-auth-gate-segment';
import { useGateSubjectValue } from '~/modules-rocco/user/auth-gate/gate-subject';
import { resolveGatePaneImage } from '~/modules-rocco/user/auth-gate/gate-pane-image';

import {
    useBookMeetingGate,
    useBookMeetingGateEnabled,
} from '../book-meeting-gate-state';
import {
    BookMeetingGateBody,
    type BookMeetingSubject,
} from './BookMeetingGateBody';
import { BookMeetingGatePane } from './BookMeetingGatePane';
import { BookMeetingGateSuccess } from './BookMeetingGateSuccess';

const BOOK_MEETING_AUTH_SOURCE = 'bookMeeting' as const;

/**
 * Booking in the revamped two-pane gate: [pick a person and a time] → [auth] →
 * [request sent].
 *
 * Every entry point opens this one modal, so a CTA anywhere on the page — the
 * detail-page accordion row, the profile header, an Our Team card — is a call
 * to {@link useOpenBookMeetingGate} with an intent, and nothing else.
 *
 * Choosing the slot happens before the wall, deliberately: a reader who has
 * already picked a person and a time has something to come back to, and the
 * account is the last step rather than the price of looking. See BC-5174.
 */
export const BookMeetingGateModal = () => {
    // The gate's atoms are sessionStorage-backed, so an intent left over from a
    // flag-on session would otherwise open this on a build meant to have no
    // gate at all. Every other gate is mounted behind the flag for this reason.
    // Same condition the CTAs read, so a CTA can never set an intent that
    // nothing mounts to answer.
    const enabled = useBookMeetingGateEnabled();
    if (!enabled) return null;

    return <BookMeetingGateModalInner />;
};

const BookMeetingGateModalInner = () => {
    const { intent, isOpen, close } = useBookMeetingGate();
    const { isLoggedIn } = useUserLoggedIn();
    const subject = useGateSubjectValue();
    /** The segment has been entered for this opening of the modal. */
    const enteredRef = useRef(false);
    /** Last intent shown, so the sheet can animate out after it is cleared. */
    const lastIntentRef = useRef<typeof intent>(null);

    /**
     * `select` until the reader asks to send. Deriving the step from the
     * segment's own state would make "not entered yet" and "resolved" the same
     * thing, which shows the success screen for a frame on open.
     */
    const [step, setStep] = useState<'select' | 'auth'>('select');
    /**
     * Bumped once the segment resolves, which tells the card to send the
     * request the reader already chose. Without it they would come back from
     * the wall to the same button and have to press it twice.
     */
    const [submitSignal, setSubmitSignal] = useState(0);
    /** Who the reader is meeting, reported up by the body once it has loaded. */
    const [subjectInfo, setSubjectInfo] = useState<BookMeetingSubject | null>(
        null,
    );
    const [sent, setSent] = useState(false);
    /** Bumped when the reader backs out, to disarm a queued send. */
    const [cancelSignal, setCancelSignal] = useState(0);

    // Written from an effect, not during render: a render-phase ref write can
    // be committed by a pass React later discards, and this ref decides what
    // the sheet shows on its way out.
    useEffect(() => {
        if (intent) lastIntentRef.current = intent;
    }, [intent]);

    const gate = useAuthGateSegment({
        authSource: BOOK_MEETING_AUTH_SOURCE,
        // Resolved covers the Secure Sign-In ask too, so the request is sent
        // once the whole segment is done rather than the moment credentials
        // land on the wire.
        onResolved: () => {
            setStep('select');
            setSubmitSignal((n) => n + 1);
        },
        // Backing out of the wall returns to the slot, still chosen, and
        // cancels the send: leaving it armed meant a later resolve fired a
        // request the reader had already walked away from.
        onAuthBack: () => {
            setStep('select');
            setCancelSignal((n) => n + 1);
        },
    });

    useEffect(() => {
        if (!isOpen) {
            gate.reset();
            enteredRef.current = false;
            setStep('select');
            setSubmitSignal(0);
            setCancelSignal(0);
            setSubjectInfo(null);
            setSent(false);
            return;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    /**
     * Continue on the card: an authenticated reader goes straight to sending,
     * a guest meets the wall with their choice already made.
     */
    const handleContinue = useCallback(() => {
        if (isLoggedIn) return true;
        if (!enteredRef.current) {
            enteredRef.current = true;
            gate.open();
        }
        setStep('auth');
        return false;
    }, [isLoggedIn, gate]);

    /**
     * The route refused the send over identity: the session is gone, or it
     * belongs to a different account than the one this page was drawn for. The
     * wall goes back up saying so — re-entering it on every refused attempt is
     * the enquiry gate's pattern too — and the body has already re-armed the
     * chosen slot, so resolving the wall sends it as whoever just signed in.
     */
    const handleNeedsLogin = useCallback(() => {
        enteredRef.current = true;
        gate.open({
            message: 'Log in to send your meeting request.',
        });
        setStep('auth');
    }, [gate]);

    const handleClose = useCallback(() => {
        gate.close();
        close();
    }, [gate, close]);

    const displayIntent = intent ?? lastIntentRef.current;
    if (!displayIntent) return null;

    const inAuth = step === 'auth' && gate.isActive;
    /**
     * Who the pane draws: the body's answer once it has one, otherwise the
     * practice the CTA already knew about. `isCompany: null` holds back the
     * half of the supporting line that depends on the answer.
     */
    const preferredName = displayIntent.preferredTeamMemberName;
    // A CTA that named someone has already told the reader who this is about,
    // so the pane says it too rather than naming the practice and correcting
    // itself once the roster lands.
    const openingName = preferredName || displayIntent.professionalName;
    const paneSubject =
        subjectInfo ??
        (openingName
            ? {
                  subjectName: openingName,
                  professionalName:
                      displayIntent.professionalName ?? openingName,
                  isCompany: preferredName ? false : null,
                  companyLogo: displayIntent.companyLogo,
                  logoBackgroundColor: displayIntent.logoBackgroundColor,
              }
            : null);
    /**
     * The body's photo is the same person's, resolved from the roster, so
     * taking it over the one the CTA handed us only swaps one crop for another
     * a beat later. Keep the page's while the body still names that person.
     */
    const bodyIsPreferred =
        Boolean(preferredName) && subjectInfo?.subjectName === preferredName;
    const paneOverride =
        displayIntent.paneImage && (bodyIsPreferred || !subjectInfo)
            ? displayIntent.paneImage
            : (subjectInfo?.image ?? displayIntent.paneImage);
    const paneImage = resolveGatePaneImage({
        override: paneOverride,
        subject,
        gate: 'download',
    });

    /*
     * One condition for both halves of the swap.
     *
     * The body used to hide on `sent` while the success step needed
     * `subjectInfo` as well, so a send that landed before the body had
     * reported who it was about left the sheet holding nothing — and a sheet
     * measured from its own content, holding nothing, reads as one that closed
     * itself on the reader. The success step takes the pane's subject, which
     * falls back to the intent the CTA opened with, so it can always draw.
     */
    const showSent = sent && Boolean(paneSubject);

    return (
        <TwoPaneModal
            corners="rounded"
            open={isOpen}
            onOpenChange={(nextOpen) => {
                if (!nextOpen) handleClose();
            }}
            title={inAuth ? 'Sign in to book your meeting' : 'Book a meeting'}
            // Behind the wall the pane makes ArchiPro's case, because that is
            // what the reader is being asked for. Either side of it the pane
            // holds who they came to meet — drawn from the intent until the
            // availability fetch answers, so the practice is named and badged
            // on the first frame rather than after a second of brand pitch.
            pane={
                inAuth || !paneSubject ? (
                    <AuthModalBrandPane
                        statement={getAuthBrandStatement(
                            BOOK_MEETING_AUTH_SOURCE,
                            isReturningPasskeyDevice(),
                        )}
                        image={paneImage}
                    />
                ) : (
                    <BookMeetingGatePane
                        image={paneImage}
                        subjectName={paneSubject.subjectName}
                        professionalName={paneSubject.professionalName}
                        isCompany={paneSubject.isCompany}
                        isBooked={subjectInfo?.isBooked ?? false}
                        companyLogo={paneSubject.companyLogo}
                        logoBackgroundColor={paneSubject.logoBackgroundColor}
                    />
                )
            }
        >
            {/* The body stays mounted behind the wall. Swapping it out for
                `gate.element` unmounted it, and with it the day and time the
                reader had already chosen — so they came back from signing in to
                an empty form and had to pick all over again. The auth step is
                part of the journey, not a detour off it. */}
            <div
                className={cn(
                    'flex h-full flex-col',
                    (inAuth || showSent) && 'hidden',
                )}
            >
                <BookMeetingGateBody
                    intent={displayIntent}
                    onSubject={setSubjectInfo}
                    cancelSignal={cancelSignal}
                    onSent={() => setSent(true)}
                    onBeforeSend={handleContinue}
                    submitSignal={submitSignal}
                    onNeedsLogin={handleNeedsLogin}
                />
            </div>
            {inAuth ? gate.element : null}
            {showSent && paneSubject ? (
                <BookMeetingGateSuccess
                    subjectName={paneSubject.subjectName}
                    professionalName={paneSubject.professionalName}
                    when={subjectInfo?.when ?? ''}
                    isBooked={subjectInfo?.isBooked ?? false}
                    onClose={handleClose}
                />
            ) : null}
        </TwoPaneModal>
    );
};
